import React, { useCallback, useEffect, useState } from 'react';
import { UserX, RefreshCw, Search, ShieldOff, Calendar, Monitor, ChevronDown, ChevronUp, Zap, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne',
    });
  } catch { return iso; }
};

const PLAN_OPTIONS = [
  { key: 'monthly', label: '🤝 Monthly' },
  { key: 'annual', label: '🪙 Annual' },
  { key: 'founding', label: '👑 Founding (lifetime)' },
];

/** Admin view of banned emails (self-delete + sharing-ban). Shows every
 *  device that ever hit the account, why they got banned, and gives one
 *  Reactivate button that restores the prior subscription instantly. */
const BannedEmailsTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [openDevicesFor, setOpenDevicesFor] = useState(null);
  const [reactivateFor, setReactivateFor] = useState(null);

  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${API}/api/admin/banned-emails`);
      if (q.trim()) url.searchParams.set('q', q.trim());
      const res = await fetch(url.toString(), { headers: auth });
      if (!res.ok) throw new Error('Failed to load ban list');
      const data = await res.json();
      setRows(data.banned || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, q]);

  useEffect(() => { load(); }, [load]);

  const unban = async (email) => {
    if (!window.confirm(`Lift the ban on ${email}?\n\nThey will be able to sign up with this email again (but no subscription is restored).`)) return;
    setBusy(`unban:${email}`);
    try {
      const res = await fetch(`${API}/api/admin/banned-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Unban failed');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="banned-emails-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <UserX className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-black tracking-widest text-red-400 uppercase">
              Banned Emails
            </h3>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">
            Anyone banned via <strong className="text-white">Delete-My-Account</strong> or
            <strong className="text-white"> Sharing-ban (3-strike ladder)</strong>. Tap
            <strong className="text-yellow-300"> Devices</strong> to see every browser that hit
            the account, or <strong className="text-emerald-300">Reactivate</strong> to restore
            their old subscription instantly if it was banned by mistake.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="banned-emails-refresh"
          className="self-start sm:self-auto flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email or reason…"
          data-testid="banned-emails-search"
          className="w-full sm:max-w-sm bg-black/50 border border-red-500/25 text-white pl-9 pr-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
        />
      </div>

      {error && (
        <div
          className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm"
          data-testid="banned-emails-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="banned-emails-loading">Loading ban list…</div>
      ) : rows.length === 0 ? (
        <div
          className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400"
          data-testid="banned-emails-empty"
        >
          No banned emails{q.trim() ? ' match that search' : ''}.
        </div>
      ) : (
        <div className="space-y-3" data-testid="banned-emails-list">
          {rows.map((r) => {
            const devicesOpen = openDevicesFor === r.email;
            const reactivating = busy === `reactivate:${r.email}`;
            const unbanning = busy === `unban:${r.email}`;
            return (
              <div
                key={r.email}
                data-testid={`banned-email-item-${r.email}`}
                className="bg-zinc-900/80 backdrop-blur-xl border border-red-500/25 rounded-lg p-3 sm:p-4 space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-mono text-sm font-black text-red-300 break-all"
                      data-testid={`banned-email-address-${r.email}`}
                    >
                      {r.email}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Banned {fmtDate(r.banned_at)}</span>
                      {r.prior_plan && <span>Prior plan: <span className="text-gray-300">{r.prior_plan}</span></span>}
                      {r.devices?.length > 0 && <span>Devices: <span className="text-yellow-300">{r.devices.length}</span></span>}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    {r.devices?.length > 0 && (
                      <button
                        onClick={() => setOpenDevicesFor(devicesOpen ? null : r.email)}
                        data-testid={`banned-email-devices-toggle-${r.email}`}
                        className="flex items-center gap-1.5 bg-zinc-800/70 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-black text-[11px] uppercase tracking-widest px-3 py-1.5 rounded whitespace-nowrap"
                      >
                        <Monitor className="h-3 w-3" />
                        Devices ({r.devices.length})
                        {devicesOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                    )}
                    <button
                      onClick={() => setReactivateFor(r)}
                      disabled={reactivating}
                      data-testid={`banned-email-reactivate-open-${r.email}`}
                      className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-black text-[11px] uppercase tracking-widest px-3 py-1.5 rounded whitespace-nowrap shadow-lg shadow-emerald-950/50"
                      title="Restore this account and its subscription instantly"
                    >
                      {reactivating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                      Reactivate + restore
                    </button>
                    <button
                      onClick={() => unban(r.email)}
                      disabled={unbanning}
                      data-testid={`banned-email-unban-${r.email}`}
                      title="Just lift the ban - does NOT restore their subscription"
                      className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-700/50 text-red-200 disabled:opacity-40 font-black text-[11px] uppercase tracking-widest px-3 py-1.5 rounded whitespace-nowrap"
                    >
                      <ShieldOff className="h-3.5 w-3.5" />
                      {unbanning ? 'Unbanning…' : 'Unban only'}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">
                    Reason
                  </p>
                  <p className="text-sm text-gray-200 leading-relaxed bg-black/40 border border-red-500/15 rounded px-3 py-2 break-words">
                    {r.reason || '-'}
                  </p>
                </div>

                {devicesOpen && r.devices?.length > 0 && (
                  <div
                    data-testid={`banned-email-devices-${r.email}`}
                    className="border border-zinc-800 rounded overflow-hidden"
                  >
                    <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 text-[10px] font-black text-zinc-500 uppercase tracking-widest bg-black/40 border-b border-zinc-800">
                      <span>Device</span>
                      <span>First seen</span>
                      <span>Last active</span>
                    </div>
                    <ul className="divide-y divide-zinc-900">
                      {r.devices.map((d) => (
                        <li
                          key={d.client_id}
                          data-testid={`banned-email-device-row-${r.email}-${d.client_id}`}
                          className="px-3 py-2 text-xs grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0.5"
                        >
                          <div className="min-w-0">
                            <p className="text-white font-black truncate">{d.label}</p>
                            <p className="text-zinc-600 text-[10px] font-mono truncate" title={d.user_agent}>{d.user_agent}</p>
                            <p className="text-zinc-700 text-[10px] font-mono truncate">IP {d.last_ip || d.first_ip || '-'} · cid {d.client_id.slice(0, 10)}…</p>
                          </div>
                          <p className="text-zinc-400 text-[11px] sm:text-right whitespace-nowrap">{fmtDate(d.first_seen)}</p>
                          <p className="text-zinc-400 text-[11px] sm:text-right whitespace-nowrap">{fmtDate(d.last_seen)}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {reactivateFor && (
        <ReactivateModal
          token={token}
          row={reactivateFor}
          onClose={() => setReactivateFor(null)}
          onDone={async () => { setReactivateFor(null); await load(); }}
          setBusy={setBusy}
          busy={busy}
          setError={setError}
        />
      )}
    </div>
  );
};

const ReactivateModal = ({ token, row, onClose, onDone, setBusy, busy, setError }) => {
  const [plan, setPlan] = useState(row.prior_plan && ['monthly', 'annual', 'founding'].includes(row.prior_plan) ? row.prior_plan : 'monthly');
  const [sendLink, setSendLink] = useState(true);
  const auth = { Authorization: `Bearer ${token}` };
  const busyKey = `reactivate:${row.email}`;
  const isBusy = busy === busyKey;

  const submit = async () => {
    setBusy(busyKey);
    try {
      const res = await fetch(`${API}/api/admin/banned-emails/${encodeURIComponent(row.email)}/reactivate`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, send_magic_link: sendLink }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reactivate failed');
      let msg = `${row.email} reactivated on ${plan.toUpperCase()}.`;
      if (sendLink) msg += data.magic_link_sent ? ' Magic-link email sent.' : `\nMagic-link email FAILED: ${data.magic_link_error || 'unknown'}`;
      window.alert(msg);
      await onDone();
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="banned-email-reactivate-modal"
    >
      <div
        className="w-full max-w-md my-6 bg-zinc-950 border border-emerald-700/60 rounded-lg shadow-2xl shadow-emerald-950/40"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-5 border-b border-zinc-800">
          <p className="text-emerald-400 text-[10px] font-black uppercase tracking-[0.35em] mb-1">Reactivate + restore</p>
          <h2 className="text-white text-lg font-black break-all" data-testid="reactivate-modal-email">{row.email}</h2>
          <p className="text-zinc-400 text-xs mt-1 leading-relaxed">
            Lifts the ban, restores their subscription tier, wipes every strike + device row so they start clean.
            Use this when the ban was a mistake on our end (or theirs) and you want them back instantly.
          </p>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Restore to tier</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="reactivate-plan"
              className="w-full bg-black border border-zinc-800 focus:border-emerald-500 rounded px-3 py-2.5 text-sm text-white outline-none"
            >
              {PLAN_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            {row.prior_plan && (
              <p className="text-[11px] text-zinc-500 mt-1">
                Prior tier at ban-time: <span className="text-zinc-300 uppercase font-black">{row.prior_plan}</span>
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 text-xs text-zinc-300 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={sendLink}
              onChange={(e) => setSendLink(e.target.checked)}
              data-testid="reactivate-send-magic"
              className="accent-emerald-600 mt-0.5"
            />
            <span>
              Email them a one-tap magic-link so they can sign back in right away <span className="text-zinc-500">(recommended)</span>.
            </span>
          </label>
          {row.devices?.length > 0 && (
            <p className="text-[11px] text-zinc-500">
              {row.devices.length} device{row.devices.length === 1 ? '' : 's'} on record - all cleared on reactivate so the 3-in-14 counter restarts.
            </p>
          )}
        </div>
        <div className="p-4 sm:p-5 border-t border-zinc-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="reactivate-cancel"
            className="text-xs font-black uppercase tracking-widest bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white px-4 py-2.5 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isBusy}
            data-testid="reactivate-confirm"
            className="text-xs font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2.5 rounded inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/50"
          >
            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            Reactivate + restore
          </button>
        </div>
      </div>
    </div>
  );
};

export default BannedEmailsTab;
